import { type HandoverSpec } from '@/designs-dev/handover/types';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';

const at = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0' />
);

// On the dev concept, past where dev's build stops.
const onDev = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0-dev' />
);

/**
 * The dynamic flow, concepted: what happens after the summary once the chat
 * can work out what to ask next, and the Home it hands over to. Was the top of
 * the Onboarding 2.0 page; on its own so 2.0 reads as the conversation alone.
 */
export const dynamicFlowSpec: HandoverSpec = {
  id: 'dynamic-flow',
  title: 'Dynamic flow concepting',
  description: '',
  canvas: true,
  links: [
    { label: 'Onboarding 2.0', url: '/tell-us-once/flows/onboarding-2-0' },
  ],
  flows: [
    {
      // Was the end of Onboarding 2.0's dev concept. Dev's build goes from
      // what we've got to where to start, so this is the dynamic version of
      // that ending, kept here.
      title: "After what we've got",
      arrows: false,
      steps: [
        {
          id: 'dev-still-need',
          title: 'Still to come',
          render: onDev('help-still-need'),
        },
        {
          id: 'dev-eligible',
          title: 'What they could get',
          render: onDev('help-eligible'),
          isNew: true,
        },
      ],
    },
    {
      title: 'Dynamic flow concept',
      arrows: false,
      steps: [
        {
          id: 'help',
          title: 'What do you need help with?',
          render: at('chat-v2'),
        },
        {
          id: 'help-processing',
          title: 'Reading what they said',
          render: at('help-processing'),
        },
        {
          id: 'summary',
          title: 'Does this look right?',
          render: at('help-summary'),
        },
        {
          id: 'how-we-help',
          title: 'How we can help',
          render: at('help-how'),
        },
        {
          id: 'what-we-need',
          title: "What we'd need",
          render: at('help-need'),
        },
        {
          id: 'gather',
          title: 'Send us what you have',
          render: at('help-gather'),
        },
        {
          id: 'gathered',
          title: 'Some of it sent',
          render: at('help-gathered'),
        },
        {
          id: 'start',
          title: 'What to start with',
          render: at('help-start'),
        },
        {
          id: 'start-later',
          title: "I'll do this later",
          render: at('help-start-later'),
          isNew: true,
        },
      ],
    },
    {
      title: 'Dynamic flow home concepting',
      arrows: false,
      beside: true,
      // Versions of an idea rather than steps, so they carry their own numbers.
      numbered: false,
      isNew: true,
      steps: [
        {
          id: 'home-conversations',
          title: 'Concept 1.0, opening the app again',
          tint: 'lavender.main',
          render: at('home-conversations'),
          isNew: true,
        },
        {
          id: 'home-conversations-menu',
          title: 'Concept 1.1, the menu open',
          tint: 'lavender.main',
          render: at('home-conversations-menu'),
          isNew: true,
        },
        {
          id: 'home-concept-2',
          title: 'Concept 2, calls and conversations',
          tint: 'warmOrange.main',
          render: at('home-concept-2'),
          isNew: true,
        },
        {
          id: 'home-concept-2-todo',
          title: 'Concept 3, still to send and the menu',
          tint: 'cherry.main',
          render: at('home-concept-2-todo'),
          isNew: true,
        },
        {
          id: 'home-c3-typing',
          title: 'Concept 3.1, typing',
          tint: 'cherry.main',
          render: at('home-c3-typing'),
          isNew: true,
        },
        {
          id: 'home-c3-sent',
          title: 'Concept 3.1, one of the list, ticked',
          tint: 'cherry.main',
          render: at('home-c3-sent'),
          isNew: true,
        },
        {
          id: 'home-c3-unsure',
          title: 'Concept 3.1, not sure, so it asks',
          tint: 'cherry.main',
          render: at('home-c3-unsure'),
          isNew: true,
        },
        {
          id: 'home-c3-put-away',
          title: 'Concept 3.1, the list put away',
          tint: 'cherry.main',
          render: at('home-c3-put-away'),
          isNew: true,
        },
      ],
    },
    {
      title: 'Dynamic flow home concepting, what she could get',
      arrows: false,
      under: true,
      numbered: false,
      isNew: true,
      steps: [
        {
          id: 'home-c4',
          title: 'Concept 4, what she could get',
          tint: 'primary.main',
          render: at('home-c4'),
          isNew: true,
        },
        {
          id: 'home-c4-aa',
          title: 'Concept 4.1, its own conversation',
          tint: 'primary.main',
          render: at('home-c4-aa'),
          isNew: true,
        },
        {
          id: 'home-c4-aa-part',
          title: 'Concept 4.2, a part started, the card on it',
          tint: 'primary.main',
          render: at('home-c4-aa-part'),
          isNew: true,
        },
        {
          id: 'home-c4-aa-all',
          title: 'Concept 4.3, back to all of it',
          tint: 'primary.main',
          render: at('home-c4-aa-all'),
          isNew: true,
        },
      ],
    },
  ],
  states: [],
  components: {},
};
